import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Timer, Users, Brain, X, Loader2, Clock, Target, Bot } from 'lucide-react';
import { useMatchmaking } from '@/hooks/useMatchmaking';
import { useAuth } from '@/hooks/useAuth';

interface MatchmakingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMatchFound: (
    gameSessionId: string,
    timeControl: string,
    playerColor: 'white' | 'black',
    isBot?: boolean,
    botElo?: number,
    fakeOpponent?: { username: string; elo: number }
  ) => void;
}

const timeControls = [
  { label: '1+0 Bullet', value: '1+0', icon: '⚡', description: 'Lightning fast' },
  { label: '3+0 Blitz', value: '3+0', icon: '🔥', description: 'Quick games' },
  { label: '5+0 Blitz', value: '5+0', icon: '⭐', description: 'Most popular' },
  { label: '10+0 Rapid', value: '10+0', icon: '♟️', description: 'Think time' },
  { label: '15+10 Rapid', value: '15+10', icon: '⏰', description: 'With increment' },
  { label: '30+0 Classical', value: '30+0', icon: '👑', description: 'Deep thinking' }
];

export function MatchmakingDialog({ open, onOpenChange, onMatchFound }: MatchmakingDialogProps) {
  const [selectedTimeControl, setSelectedTimeControl] = useState('5+0');
  const { user } = useAuth();
  const { 
    userProfile, 
    isInQueue, 
    isSearching, 
    matchFound, 
    timeInQueue,
    eloTolerance,
    queueStats,
    joinQueue, 
    leaveQueue,
    offerBotMatch
  } = useMatchmaking();

  const handleJoinQueue = async () => {
    if (!selectedTimeControl) return;
    
    const result = await joinQueue(selectedTimeControl);
    if (result) {
      if (result.type === 'match_found') {
        onMatchFound(
          result.game_session_id,
          result.time_control,
          result.player_color,
          false
        );
        onOpenChange(false);
      }
    }
  };

  const handlePlayBot = () => {
    if (!userProfile) return;
    
    offerBotMatch(selectedTimeControl);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Fallback timeout: If still searching after 25 seconds, force bot match
  useEffect(() => {
    if (!isSearching || !isInQueue) return;
    
    console.log('🛡️ Fallback timeout started (25s)');
    const fallbackTimeout = setTimeout(() => {
      if (isSearching && isInQueue) {
        console.log('⚠️ Fallback: forcing bot match after 25s');
        offerBotMatch(selectedTimeControl);
      }
    }, 25000);
    
    return () => clearTimeout(fallbackTimeout);
  }, [isSearching, isInQueue, selectedTimeControl, offerBotMatch]);

  if (!user) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Play Online
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">Please log in to play online</p>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Handle match found
  if (matchFound) {
    const isBot = matchFound.is_bot_game || matchFound.black_player_id === 'bot';
    const botElo = matchFound.bot_elo;
    
    setTimeout(() => {
      if (isBot) {
        onMatchFound(
          `BOT-${Date.now().toString(36).toUpperCase()}-ELO${botElo}`,
          matchFound.time_control,
          matchFound.player_color,
          true,
          botElo,
          matchFound.fakeOpponent
        );
      } else {
        onMatchFound(
          matchFound.game_session_id,
          matchFound.time_control,
          matchFound.player_color,
          false
        );
      }
      onOpenChange(false);
    }, 1000);
    
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-success">
              <Users className="w-5 h-5" />
              Match Found!
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
              {isBot ? <Brain className="w-8 h-8 text-success" /> : <Users className="w-8 h-8 text-success" />}
            </div>
            <p className="text-lg font-medium mb-2">
              Opponent Found!
            </p>
            {matchFound.fakeOpponent && (
              <div className="mb-2">
                <p className="font-semibold">{matchFound.fakeOpponent.username}</p>
                <Badge variant="outline" className="mt-1">{matchFound.fakeOpponent.elo} ELO</Badge>
              </div>
            )}
            <p className="text-muted-foreground">Starting your {matchFound.time_control} game...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Searching state
  if (isInQueue || isSearching) {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Searching for Opponent
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <div>
                <p className="font-medium">Searching for opponent...</p>
                <p className="text-sm text-muted-foreground">
                  Time in queue: {formatTime(timeInQueue)}
                </p>
              </div>
            </div>

            {/* Queue Statistics */}
            {queueStats && (
              <div className="grid grid-cols-2 gap-3">
                <Card className="p-3">
                  <div className="text-center">
                    <Users className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-2xl font-bold">{queueStats.total_waiting}</p>
                    <p className="text-xs text-muted-foreground">Players Waiting</p>
                  </div>
                </Card>

                <Card className="p-3">
                  <div className="text-center">
                    <Clock className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-2xl font-bold">
                      {Math.round(queueStats.estimated_wait_seconds)}s
                    </p>
                    <p className="text-xs text-muted-foreground">Est. Wait Time</p>
                  </div>
                </Card>
              </div>
            )}

            {/* ELO Tolerance Display */}
            <Alert>
              <Target className="h-4 w-4" />
              <AlertDescription>
                Searching for opponents ±{eloTolerance} ELO
                {eloTolerance < 400 && (
                  <span className="block text-xs mt-1">
                    (expanding range every 15s)
                  </span>
                )}
              </AlertDescription>
            </Alert>

            <Button onClick={leaveQueue} variant="outline" className="w-full">
              <X className="w-4 h-4 mr-2" />
              Cancel Search
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Time control selection
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Play Online
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {userProfile && (
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{userProfile.display_name || 'Player'}</p>
                    <p className="text-sm text-muted-foreground">
                      Rating: {userProfile.elo_rating} • Games: {userProfile.games_played}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-sm">
                    W: {userProfile.games_won} • L: {userProfile.games_lost}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          <div>
            <h3 className="font-medium mb-3">Choose Time Control</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {timeControls.map((control) => (
                <Card
                  key={control.value}
                  className={`cursor-pointer transition-colors ${
                    selectedTimeControl === control.value
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-muted/50'
                  }`}
                  onClick={() => setSelectedTimeControl(control.value)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{control.icon}</span>
                      <div className="flex-1">
                        <p className="font-medium">{control.label}</p>
                        <p className="text-xs text-muted-foreground">{control.description}</p>
                      </div>
                      {selectedTimeControl === control.value && (
                        <div className="w-2 h-2 bg-primary rounded-full" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <Separator />

          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleJoinQueue}
              className="flex-1"
              disabled={!selectedTimeControl}
            >
              <Users className="w-4 h-4 mr-2" />
              Find Opponent
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}